import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2,
  ArrowRight,
  CheckCircle2,
  Play,
  Award,
} from "lucide-react";
import { sanitizeHtml } from "@/utils/sanitize";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Two-panel split dashboard: assigned tracks on the left, personal
// results (horizontal bar chart) on the right. Both panels share the
// same fixed height and internally scroll — the left panel's cards stack
// from the top, the right panel's bars are distributed evenly across
// the full panel height so short lists don't cluster at the top.
const PANEL_HEIGHT = "480px";

export default function LearnerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [welcome, setWelcome] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, welcomeRes, meRes, resultsRes] = await Promise.all([
        axios.get(`${API}/learner/dashboard`, { withCredentials: true }),
        axios.get(`${API}/learner/welcome`, { withCredentials: true }).catch(() => ({ data: { show_welcome: false } })),
        axios.get(`${API}/learner/me`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/learner/results`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setData(dashRes.data);
      setWelcome(welcomeRes.data);
      setMe(meRes.data);
      setResults(Array.isArray(resultsRes.data) ? resultsRes.data : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Dashboard load error:", err?.response?.status, err?.response?.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleDismissWelcome = async () => {
    setDismissing(true);
    try {
      await axios.post(`${API}/learner/welcome/dismiss`, {}, { withCredentials: true });
      setWelcome((p) => ({ ...p, show_welcome: false }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to dismiss welcome:", err);
    } finally { setDismissing(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--theme-primary, var(--primary-color, #4A90D9))" }} />
      </div>
    );
  }
  if (!data) {
    return <p className="text-center text-[#6E7787] font-body py-12">Unable to load dashboard.</p>;
  }

  const tracks = data.tracks || [];
  const dashboardMessage = data.dashboard_message || "";

  const firstName = me?.first_name || "";
  const lastName = me?.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim() || "Learner";
  const cohortName = me?.cohort_name || "";

  // Only include results that have a real numeric score so the chart
  // doesn't render empty bars for pending assessments.
  const scoredResults = results.filter((r) => typeof r.score === "number");

  return (
    <div className="space-y-6" data-testid="learner-dashboard">
      {/* Page header — learner name + cohort (replaces "To Do") */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-testid="dashboard-header">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900" data-testid="dashboard-learner-name">
          {fullName}
        </h1>
        {cohortName && (
          <span className="text-sm sm:text-base font-body text-gray-500" data-testid="dashboard-cohort-name">
            {cohortName}
          </span>
        )}
      </div>

      {/* Welcome block (unchanged behaviour) */}
      {welcome && welcome.show_welcome && (
        <div
          className="rounded-xl p-6 border relative overflow-hidden"
          style={{
            backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 8%, var(--user-bg, #F8F9FC))",
            borderColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 20%, transparent)",
          }}
          data-testid="dashboard-welcome-block"
        >
          {welcome.welcome_message && (
            <div
              className={`rounded-xl border border-gray-200 bg-white p-5 ${welcome.lexps && welcome.lexps.length > 0 ? "mb-4" : ""}`}
              data-testid="dashboard-welcome-card"
            >
              <div
                className="font-body text-gray-800 ql-rendered break-words overflow-wrap-anywhere max-w-full"
                style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(welcome.welcome_message) }}
              />
            </div>
          )}
          {welcome.lexps && welcome.lexps.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-body font-semibold text-gray-500 uppercase tracking-wider">Getting Started</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {welcome.lexps.map((lexp) => (
                  <button
                    key={lexp.id}
                    onClick={() => navigate(`/portal/learning/${lexp.id}`)}
                    disabled={lexp.completed}
                    className={`text-left rounded-lg border p-3 transition-all ${
                      lexp.completed
                        ? "bg-white/50 border-gray-200 opacity-60"
                        : "bg-white border-gray-200 hover:shadow-md hover:border-gray-300 cursor-pointer"
                    }`}
                    data-testid={`welcome-lexp-${lexp.id}`}
                  >
                    <div className="flex items-center gap-2">
                      {lexp.completed
                        ? <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                        : <Play className="h-4 w-4 flex-shrink-0" style={{ color: "var(--theme-primary, var(--primary-color, #4A90D9))" }} />}
                      <span className={`text-sm font-body font-medium truncate ${lexp.completed ? "text-gray-400 line-through" : "text-gray-900"}`}>
                        {lexp.title}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              {welcome.all_complete && (
                <p className="text-xs text-green-600 font-body flex items-center gap-1 mt-2">
                  <CheckCircle2 className="h-3 w-3" /> All welcome tasks complete!
                </p>
              )}
            </div>
          )}
          <div className="flex justify-end mt-4">
            <button
              onClick={handleDismissWelcome}
              disabled={dismissing}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-body font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}
              data-testid="welcome-dismiss-btn"
            >
              {dismissing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Let's Get To Work
            </button>
          </div>
        </div>
      )}

      {/* Global dashboard message (unchanged) */}
      {dashboardMessage && (!welcome || !welcome.show_welcome) && (
        <div
          className="rounded-xl p-6 border"
          style={{
            backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 10%, var(--user-bg, #F8F9FC))",
            borderColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 20%, transparent)",
          }}
          data-testid="dashboard-welcome"
        >
          <div className="font-body text-gray-800 ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(dashboardMessage) }} />
        </div>
      )}

      {/* Two-panel split — fixed height, side by side on lg+ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dashboard-split">
        <TracksPanel tracks={tracks} navigate={navigate} />
        <ResultsPanel scoredResults={scoredResults} navigate={navigate} />
      </div>
    </div>
  );
}

// ─── Left panel ──────────────────────────────────────────────────────────
function TracksPanel({ tracks, navigate }) {
  return (
    <div
      className="rounded-xl border border-gray-200 bg-white flex flex-col overflow-hidden"
      style={{ height: PANEL_HEIGHT }}
      data-testid="tracks-panel"
    >
      <div className="px-4 sm:px-5 py-3 border-b border-gray-100 flex-shrink-0">
        <p className="text-[10px] font-body uppercase tracking-widest text-gray-400">Assigned Tracks</p>
      </div>
      {tracks.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-8 text-center px-4">
          <CheckCircle2 className="h-6 w-6 text-emerald-400 mb-2" />
          <p className="text-xs text-gray-400 font-body">No tracks assigned.</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-3" data-testid="tracks-panel-list">
          {tracks.map((t) => (
            <TrackCard key={t.id} track={t} navigate={navigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function TrackCard({ track, navigate }) {
  const total = track.total_items || 0;
  const completed = track.completed_items || 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isComplete = total > 0 && completed >= total;
  const hasStarted = (track.has_started || completed > 0) && !isComplete;

  // Status → ring + CTA styling. Not-started uses a gray ring with no
  // fill (arc drawn as background only). In-progress fills with theme
  // blue. Complete fills with green.
  let ringColor = "#E5E7EB"; // gray-200 for not-started
  let ctaLabel = "Begin →";
  let ctaClass = "bg-emerald-500 text-white hover:bg-emerald-600";
  let showArc = false;
  if (isComplete || track.submitted) {
    ringColor = "#10B981"; // green
    ctaLabel = "Review";
    ctaClass = "bg-gray-100 text-gray-700 hover:bg-gray-200";
    showArc = true;
  } else if (hasStarted) {
    ringColor = "#4A90D9"; // theme blue
    ctaLabel = "Continue →";
    ctaClass = "text-white hover:opacity-90";
    showArc = true;
  }

  // Ring geometry.
  const RING_SIZE = 44;
  const STROKE = 4;
  const R = (RING_SIZE - STROKE) / 2;
  const C = 2 * Math.PI * R;
  const dash = showArc ? (pct / 100) * C : 0;

  return (
    <div
      className="w-full rounded-lg border border-gray-100 bg-white p-3 flex items-center gap-3"
      data-testid={`track-card-${track.id}`}
    >
      {/* Ring */}
      <div
        className="flex-shrink-0 relative"
        style={{ width: RING_SIZE, height: RING_SIZE }}
        data-testid={`track-card-ring-${track.id}`}
      >
        <svg width={RING_SIZE} height={RING_SIZE} className="-rotate-90">
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={R}
            fill="none"
            stroke="#F3F4F6"
            strokeWidth={STROKE}
          />
          {showArc && (
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={R}
              fill="none"
              stroke={ringColor}
              strokeWidth={STROKE}
              strokeDasharray={`${dash} ${C - dash}`}
              strokeLinecap="round"
              data-testid={`track-card-ring-arc-${track.id}`}
            />
          )}
        </svg>
        <span
          className="absolute inset-0 flex items-center justify-center text-[11px] font-body font-semibold tabular-nums"
          style={{ color: showArc ? ringColor : "#9CA3AF" }}
          data-testid={`track-card-ring-label-${track.id}`}
        >
          {pct}%
        </span>
      </div>

      {/* Middle: name + activity count */}
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-body font-medium text-gray-900 truncate"
          title={track.title}
          data-testid={`track-card-name-${track.id}`}
        >
          {track.title}
        </p>
        <p
          className="text-[11px] text-gray-500 font-body mt-0.5"
          data-testid={`track-card-count-${track.id}`}
        >
          {completed} of {total} complete
        </p>
      </div>

      {/* CTA button — 30% of card width */}
      <button
        type="button"
        onClick={() => navigate(`/portal/tracks/${track.id}`)}
        className={`flex-shrink-0 basis-[30%] text-center text-xs font-body font-semibold py-2 px-3 rounded-md transition-colors ${ctaClass}`}
        style={ctaClass.includes("bg-") ? undefined : { backgroundColor: "#4A90D9" }}
        data-testid={`track-card-action-${track.id}`}
      >
        {ctaLabel}
      </button>
    </div>
  );
}

// ─── Right panel ─────────────────────────────────────────────────────────
function ResultsPanel({ scoredResults, navigate }) {
  return (
    <div
      className="rounded-xl border border-gray-200 bg-white flex flex-col overflow-hidden"
      style={{ height: PANEL_HEIGHT }}
      data-testid="results-panel"
    >
      <div className="px-4 sm:px-5 py-3 border-b border-gray-100 flex-shrink-0 flex items-center justify-between">
        <p className="text-[10px] font-body uppercase tracking-widest text-gray-400">My Results</p>
        {scoredResults.length > 0 && (
          <Award className="h-3.5 w-3.5 text-gray-300" />
        )}
      </div>
      {scoredResults.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-8 text-center px-4">
          <Award className="h-6 w-6 text-gray-300 mb-2" />
          <p className="text-xs text-gray-400 font-body">
            No results yet. Complete an assessment to see your scores here.
          </p>
        </div>
      ) : (
        // The chart body uses `justify-between` so bar rows are distributed
        // evenly across the entire available height, per requirement.
        <div
          className="flex-1 min-h-0 px-4 sm:px-5 py-4 flex flex-col justify-between gap-2 overflow-hidden"
          data-testid="results-panel-chart"
        >
          {scoredResults.map((r, idx) => (
            <ResultBar
              key={`${r.assessment_id || r.assessment_name}-${idx}`}
              row={r}
              testId={`results-bar-${idx}`}
            />
          ))}
        </div>
      )}
      <div className="px-4 sm:px-5 py-3 border-t border-gray-100 flex-shrink-0 flex justify-end">
        <button
          type="button"
          onClick={() => navigate("/portal/results")}
          className="text-xs font-body font-semibold inline-flex items-center gap-1 hover:underline"
          style={{ color: "var(--primary-color, #4A90D9)" }}
          data-testid="results-panel-view-all"
        >
          View all results <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function ResultBar({ row, testId }) {
  const score = Math.max(0, Math.min(100, Math.round(row.score)));
  const passing = score >= 70;
  // Green for >= 70%, amber for below. Tailwind palette references.
  const barColor = passing ? "#10B981" : "#F59E0B";
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <span
        className="text-xs font-body text-gray-800 truncate flex-shrink-0"
        style={{ width: "38%" }}
        title={row.assessment_name}
        data-testid={`${testId}-name`}
      >
        {row.assessment_name || "Assessment"}
      </span>
      <div className="flex-1 relative h-3 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all"
          style={{ width: `${score}%`, backgroundColor: barColor }}
          data-testid={`${testId}-fill`}
        />
      </div>
      <span
        className="text-xs font-body font-semibold tabular-nums flex-shrink-0"
        style={{ minWidth: "3.5ch", color: barColor }}
        data-testid={`${testId}-score`}
      >
        {score}%
      </span>
    </div>
  );
}
